import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../../services/api';
import { ArrowLeft, Plus, Package, Edit2, Trash2, AlertCircle } from 'lucide-react';
import '../../../styles/admin/ERPPackages.css';


const ERPPackages = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    department_id: '',
    type_tarification: 'pi2',
    prix_vente_par_1000_pi2: '',
    services_in_package: [], // [{service_id, pourcentage}]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, departmentsRes] = await Promise.all([
        api.get('/api/erp/services?actif_only=true'),
        api.get('/api/erp/departments')
      ]);
      
      const allServices = servicesRes.data.services;
      
      // Séparer services simples et forfaits
      const simpleServices = allServices.filter(s => s.type_service !== 'forfait');
      const forfaits = allServices.filter(s => s.type_service === 'forfait');
      
      setServices(simpleServices);
      setPackages(forfaits);
      setDepartments(departmentsRes.data.departments);
    } catch (error) {
      if (error.response?.status === 401) {
        
        navigate('/auth/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePackage = () => {
    setEditingPackage(null);
    setFormData({
      nom: '',
      description: '',
      department_id: '',
      type_tarification: 'pi2',
      prix_vente_par_1000_pi2: '',
      services_in_package: [],
    });
    setShowModal(true);
  };

  const handleEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      nom: pkg.nom,
      description: pkg.description || '',
      department_id: pkg.department_id || '',
      type_tarification: pkg.type_tarification,
      prix_vente_par_1000_pi2: pkg.prix_vente_par_1000_pi2 || '',
      services_in_package: pkg.services_in_package || [],
    });
    setShowModal(true);
  };

  const toggleServiceSelection = (serviceId) => {
    const exists = formData.services_in_package.find(s => s.service_id === serviceId);
    
    if (exists) {
      // Remove service
      setFormData({
        ...formData,
        services_in_package: formData.services_in_package.filter(s => s.service_id !== serviceId)
      });
    } else {
      // Add service with default percentage
      const remainingServices = formData.services_in_package.length;
      const defaultPercentage = remainingServices === 0 ? 100 : 0;
      
      setFormData({
        ...formData,
        services_in_package: [
          ...formData.services_in_package,
          { service_id: serviceId, pourcentage: defaultPercentage }
        ]
      });
    }
  };

  const updateServicePercentage = (serviceId, pourcentage) => {
    setFormData({
      ...formData,
      services_in_package: formData.services_in_package.map(s =>
        s.service_id === serviceId ? { ...s, pourcentage: parseFloat(pourcentage) || 0 } : s
      )
    });
  };

  const calculateTotalPercentage = () => {
    return formData.services_in_package.reduce((sum, s) => sum + (s.pourcentage || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.services_in_package.length === 0) {
      alert('Veuillez sélectionner au moins un service');
      return;
    }

    const totalPercentage = calculateTotalPercentage();
    if (Math.abs(totalPercentage - 100) > 0.01) {
      alert(`La répartition doit totaliser 100%. Actuellement: ${totalPercentage.toFixed(2)}%`);
      return;
    }
    
    try {
      const packageData = {
        nom: formData.nom,
        description: formData.description,
        department_id: formData.department_id,
        type_tarification: formData.type_tarification,
        type_service: 'forfait',
        prix_vente_par_1000_pi2: parseFloat(formData.prix_vente_par_1000_pi2) || 0,
        cout_produit_par_1000_pi2: 0,
        services_in_package: formData.services_in_package.map(s => s.service_id),
        repartition_prix: formData.services_in_package,
        actif: true,
        estimation_temps_minutes: 0,
        nombre_employes_defaut: 1,
        taux_horaire_employe: 80
      };

      if (editingPackage) {
        await api.put(
          `/api/erp/services/${editingPackage.id}`,
          packageData,
        );
      } else {
        await api.post(
          '/api/erp/services',
          packageData,
        );
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la sauvegarde du forfait');
      console.error(error);
    }
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce forfait?')) {
      return;
    }

    try {
      await api.delete(`/api/erp/services/${packageId}`, {
      });
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression du forfait');
    }
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service ? service.nom : 'Service inconnu';
  };

  const totalPercentage = calculateTotalPercentage();
  const isValidTotal = Math.abs(totalPercentage - 100) < 0.01;

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="erp-packages-page">
      <div className="erp-packages-header">
        <div className="erp-packages-header-content">
          <Link to="/admin/erp" className="erp-back-link">
            <ArrowLeft size={20} />
            Retour au dashboard
          </Link>
          <div className="header-title-section">
            <h1><Package size={32} /> Forfaits</h1>
            <p>Créer des packages de services avec répartition du prix</p>
          </div>
        </div>
      </div>

      <div className="erp-packages-content">
        <div className="packages-toolbar">
          <div className="packages-count">
            {packages.length} forfait(s)
          </div>
          <button className="btn-primary" onClick={handleCreatePackage}>
            <Plus size={20} />
            Nouveau forfait
          </button>
        </div>

        <div className="packages-grid">
          {packages.length === 0 ? (
            <div className="empty-state">
              <Package size={48} color="#ccc" />
              <p>Aucun forfait créé</p>
              <button className="btn-primary" onClick={handleCreatePackage}>
                <Plus size={20} />
                Créer le premier forfait
              </button>
            </div>
          ) : (
            packages.map(pkg => (
              <div key={pkg.id} className="package-card">
                <div className="package-card-header">
                  <h3>{pkg.nom}</h3>
                  <div className="package-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleEditPackage(pkg)}
                      title="Modifier"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      className="btn-icon btn-icon-danger"
                      onClick={() => handleDelete(pkg.id)}
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                <div className="package-card-body">
                  {pkg.description && <p>{pkg.description}</p>}
                  <div className="package-info">
                    <strong>Prix:</strong> {pkg.prix_vente_par_1000_pi2 || 0} $ / 1000 pi²
                  </div>
                  <div className="package-info">
                    <strong>Services inclus:</strong> {(pkg.services_in_package || []).length}
                  </div>
                  <div className="services-list-preview">
                    {(pkg.services_in_package || []).slice(0, 3).map(serviceId => {
                      const service = services.find(s => s.id === serviceId);
                      return service ? (
                        <span key={serviceId} className="service-tag">{service.nom}</span>
                      ) : null;
                    })}
                    {(pkg.services_in_package || []).length > 3 && (
                      <span className="service-tag more">+{(pkg.services_in_package || []).length - 3}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal Créer/Modifier Forfait */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPackage ? 'Modifier le forfait' : 'Nouveau forfait'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="package-form">
              {/* Basic Info */}
              <div className="form-section">
                <h3>📋 Informations de base</h3>
                
                <div className="form-group">
                  <label>Nom du forfait *</label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    placeholder="Ex: Forfait Entretien Complet"
                  />
                </div>

                <div className="form-group">
                  <label>Département *</label>
                  <select
                    value={formData.department_id}
                    onChange={(e) => setFormData({...formData, department_id: e.target.value})}
                    required
                  >
                    <option value="">-- Sélectionner un département --</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.nom}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows="2"
                    placeholder="Description du forfait..."
                  />
                </div>

                <div className="form-group">
                  <label>Prix de vente / 1000 pi² ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.prix_vente_par_1000_pi2}
                    onChange={(e) => setFormData({...formData, prix_vente_par_1000_pi2: e.target.value})}
                    placeholder="50.00"
                  />
                </div>
              </div>

              {/* Services Selection */}
              <div className="form-section">
                <h3>🛠️ Services inclus</h3>
                <p className="section-note">Sélectionnez les services qui composent ce forfait</p>
                
                <div className="services-selection-grid">
                  {services.map(service => {
                    const isSelected = formData.services_in_package.find(s => s.service_id === service.id);
                    return (
                      <div 
                        key={service.id}
                        className={`service-selection-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleServiceSelection(service.id)}
                      >
                        <div className="service-checkbox">
                          {isSelected && '✓'}
                        </div>
                        <div className="service-name">{service.nom}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Price Distribution */}
              {formData.services_in_package.length > 0 && (
                <div className="form-section highlight-section">
                  <h3>💰 Répartition du prix (doit totaliser 100%)</h3>
                  <p className="section-note">
                    Définissez le pourcentage du prix total pour chaque service. 
                    La somme doit être exactement 100%.
                  </p>
                  
                  <div className="price-distribution">
                    {formData.services_in_package.map((serviceItem, index) => (
                      <div key={serviceItem.service_id} className="distribution-row">
                        <div className="service-label">
                          {index + 1}. {getServiceName(serviceItem.service_id)}
                        </div>
                        <div className="percentage-input-group">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={serviceItem.pourcentage}
                            onChange={(e) => updateServicePercentage(serviceItem.service_id, e.target.value)}
                            className="percentage-input"
                          />
                          <span className="percentage-symbol">%</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className={`total-percentage ${isValidTotal ? 'valid' : 'invalid'}`}>
                    <div className="total-label">Total:</div>
                    <div className="total-value">
                      {totalPercentage.toFixed(2)}%
                      {isValidTotal ? (
                        <span className="status-icon valid">✓</span>
                      ) : (
                        <span className="status-icon invalid">
                          <AlertCircle size={20} />
                        </span>
                      )}
                    </div>
                  </div>

                  {!isValidTotal && (
                    <div className="validation-message">
                      ⚠️ La répartition doit totaliser exactement 100%
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={!isValidTotal || formData.services_in_package.length === 0}
                >
                  {editingPackage ? 'Mettre à jour' : 'Créer le forfait'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPPackages;
